import { Card, Typography, Input, Button, Form, DatePicker, Table } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import "jspdf-autotable";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { changeLoader } from "../../../../../../../redux/reducers/loader";
import toast from "react-hot-toast";
import globalRequest from "../../../../../../../utils/global/globalRequest";
import { debounce } from "lodash";
import * as Apis from "../../../../../../../utils/helper/enum";
import { useDispatch } from "react-redux";
import { paginationLimit } from "../../../../../../../utils/helper/constants";
import dayjs from "dayjs";
import * as Validation from "../../../../../../../utils/helper/validation";
import { convertSlote, exportCSV, exportPDf, formatDateFunction, getMapLink, getTabFromOrderStatus, roundToDecimal } from "../../../../../../../utils/helper/utils";

const expandColumns = [
  {
    title: "Order Number",
    dataIndex: "OrderNumber",
    key: "OrderNumber",
    render: (text, record) => (
      <Link className="cursor-pointer" to={`/order-management/?order_number=${record?.OrderNumber}`}
        state={{
          orderNumber: record?.OrderNumber,
          activeTab: getTabFromOrderStatus(record?.Status)
        }}
        style={{ color: "#37846B", fontWeight: 500 }}
      >
        {text}
      </Link>
    ),
  },
  {
    title: "Branch Name",
    dataIndex: "BranchName",
    key: "BranchName",
  },
  {
    title: "Driver Name",
    dataIndex: "DriverName",
    key: "DriverName",
  },
  {
    title: "Delivery Mode",
    dataIndex: "DeliveryMode",
    key: "DeliveryMode",
  },
  {
    title: "Dropzone",
    dataIndex: "Dropzone",
    key: "Dropzone",
  },
  {
    title: "Zone",
    dataIndex: "Zone",
    key: "Zone",
  },
  {
    title: "Payment Method",
    dataIndex: "PaymentMethod",
    key: "PaymentMethod",
    render: (method) => {
        if (method === "credit_card") return "Credit Card";
        if (method === "apple_pay") return "Apple Pay";
        return method || "-";
    },
  },
  {
    title: "Status",
    dataIndex: "Status",
    key: "Status",
  },
  {
    title: "Total Amount",
    dataIndex: "TotalAmount",
    key: "TotalAmount",
  },
  {
    title: "Order Date",
    dataIndex: "OrderDate",
    key: "OrderDate",
  },
  {
    title: "Delivery Date",
    dataIndex: "DeliveryDate",
    key: "DeliveryDate",
  },
  {
    title: "Time Slot",
    dataIndex: "TimeSlot",
    key: "TimeSlot",
  },
  {
    title: "Scheduled Pickup",
    dataIndex: "ScheduledPickup",
    key: "ScheduledPickup",
  },
  {
    title: "Ready To Ship",
    dataIndex: "ReadyToShip",
    key: "ReadyToShip",
  },
  {
    title: "Shipped Time",
    dataIndex: "ShippedTime",
    key: "ShippedTime",
  },
  {
    title: "Customer OTP Time",
    dataIndex: "CutomerOtpTime",
    key: "CutomerOtpTime",
  },
  {
    title: "Delivered Time",
    dataIndex: "DeliveredTime",
    key: "DeliveredTime",
  },
  {
    title: "Branch Location",
    dataIndex: "BranchLocation",
    key: "BranchLocation",
    render: (text) => {
      const link = getMapLink(text);
      return link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: "#37846B", fontWeight: 500 }}>
          {text}
        </a>
      ) : "-";
    },
  },
  {
    title: "Customer Location",
    dataIndex: "CustomerLocation",
    key: "CustomerLocation",
    render: (text) => {
      const link = getMapLink(text);
      return link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: "#37846B", fontWeight: 500 }}>
          {text}
        </a>
      ) : "-";
    },
  },
];

const DriverDataReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { driverId  } = useParams();

  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const { Search } = Input;
  const [dataSource, setDataSource] = useState([]);
  const [paginationModel] = useState(paginationLimit);
  const { search, start_date, end_date } = location.state?.inputData || {};
  const tabIndex = location.state?.tabIndex;
  const [inputData, setInputData] = useState({
    search: search,
    start_date: start_date,
    end_date: end_date, 
  });

  const handleDateChange = (dates, dateStrings) => {
    const [startdate, enddate] = dateStrings; 

    setInputData((prevState) => ({
      ...prevState,
      start_date: startdate, 
      end_date: enddate, 
    }));
  };
  const handleSearchDebounced = debounce((value) => {
    setInputData((prev) => ({ ...prev, search: value }));
  }, 300); 

  const getCustomerLocation = (order) => {
    if (
      order?.addressDetails &&
      Object.keys(order.addressDetails).length > 0 &&
      order.addressDetails?.lat &&
      order.addressDetails?.lng
    ) {
      return `${order.addressDetails.lat}-${order.addressDetails.lng}`;
    }

    if (order?.dropzoneData?.latitude && order?.dropzoneData?.longitude) {
      return `${order.dropzoneData.latitude}-${order.dropzoneData.longitude}`;
    }
    return "N/A";
  };

  const fetchOrdersForDriverDetails = useCallback(async (record) => {
    dispatch(changeLoader(true));
    try {
        const response = await globalRequest(
        `${Apis.MANAGE_REPORT.GET_ALL_PAYMENTS_REPORT_BY_DRIVER_ORDERS}/${driverId}`,
        "get", {},
        { params: { ...inputData, page: paginationModel.page, limit: paginationModel.limit } },
        true
        );
        if (response?.data?.status === "SUCCESS") {
        const orders =
            response?.data?.data?.data?.data?.map((order, index) => {
            const zoneData = order?.zoneData;
            const dropzoneData = order?.dropzoneData;
            let zoneName = "-";
            if (Array.isArray(zoneData) && zoneData.length > 0) {
                zoneName = zoneData[0]?.name || "-";
            } else if (zoneData?.name) {
                zoneName = zoneData.name;
            }
            const dropzoneName = dropzoneData?.name || "-";
            const BranchLocation = order?.branchDetails?.lat && order?.branchDetails?.lng ? `${order.branchDetails.lat}-${order.branchDetails.lng}` : "N/A";
            const CustomerLocation = getCustomerLocation(order);

            return {
                key: index,
                OrderNumber: order.orderNumber,
                BranchName: order.branchDetails?.name || "-",
                DriverName: order.driverName,
                OrderType: order.orderType,
                DeliveryMode: order.deliveryMode,
                Dropzone: dropzoneName,
                Zone: zoneName,
                PaymentMethod: order.paymentMethod,
                Status: order.status,
                TotalAmount: roundToDecimal(order.totalAmount, 2),
                OrderDate: order.orderDate,
                DeliveryDate: order.deliveryDate,

                TimeSlot: (order?.timeSlot ? order.timeSlot.split(" - ").map(time => convertSlote(time.trim())).join(" - ") : "N/A" ),
                ScheduledPickup: order.driverPickupTime ? `${order.deliveryDate || "N/A"} ${convertSlote(order.driverPickupTime)}` : "-",
                ReadyToShip: (formatDateFunction(order.readyToShipTime, "dd/mm/yyyy", true)) || "-",
                ShippedTime: (formatDateFunction(order.shippedTime, "dd/mm/yyyy", true)) || "-",
                CutomerOtpTime: (formatDateFunction(order.customerOtpTime, "dd/mm/yyyy", true)) || "-",
                DeliveredTime: (formatDateFunction(order.deliveredTime, "dd/mm/yyyy", true)) || "-",
                BranchLocation: BranchLocation,
                CustomerLocation: CustomerLocation,
            };
            }) || [];

        setDataSource(orders);
        }
    } catch (e) {
        toast.error(e?.message || "Something went wrong while fetching vendor orders");
    }
    dispatch(changeLoader(false));
  }, [dispatch, driverId, inputData, paginationModel.page, paginationModel.limit]);

  const exportData = async (value) => {
  if (!value) return;
  dispatch(changeLoader(true));

  try {
    const response = await globalRequest(
      `${Apis.MANAGE_REPORT.GET_ALL_PAYMENTS_REPORT_BY_DRIVER_ORDERS}/${driverId}`,
      "get",
      {},
      {
        params: {
          ...inputData,
          page: paginationModel.page,
          limit: paginationModel.limit,
        },
      },
      true
    );

    const apiResponse = response.data;
    if (apiResponse?.status === "SUCCESS") {
      const orderList = apiResponse?.data?.data?.data || [];

      const exportData = orderList.map((item) => {
        let dropZoneName = "-";
        if (item.dropzoneData) {
          if (typeof item.dropzoneData === "object" && item.dropzoneData !== null) {
            dropZoneName =
              item.dropzoneData?.name ||
              item.dropzoneData?.zoneName ||
              item.dropzoneData?.dropZoneName ||
              item.dropzoneData?.drop_zone_name ||
              "-";
          } else if (typeof item.dropzoneData === "string") {
            dropZoneName = item.dropzoneData;
          }
        }

        let zoneName = "-";
        if (Array.isArray(item.zoneData) && item.zoneData.length > 0) {
          const zone = item.zoneData[0];
          zoneName = zone?.name || zone?.zoneName || "-";
        } else if (typeof item.zoneData === "object" && item.zoneData !== null) {
          zoneName = item.zoneData?.name || item.zoneData?.zoneName || "-";
        } else if (item.zoneName) {
          zoneName = item.zoneName;
        }

        return {
          OrderNumber: item.orderNumber || "-",
          BranchName: item.branchDetails?.name || "-",
          DriverName: item.driverName || "-",
          DeliveryMode: item.deliveryMode || "-",
          Dropzone: dropZoneName,
          Zone: zoneName,
          PaymentMethod: item.paymentMethod || "-",
          Status: item.status || "-",
          TotalAmount: roundToDecimal(item.totalAmount, 2),
          OrderDate: item.orderDate || "-",
          DeliveryDate: item.deliveryDate || "-",
          TimeSlot : (item?.timeSlot ? item.timeSlot.split(" - ").map(time => convertSlote(time.trim())).join(" - ") : "N/A" ),
          ScheduledPickup: item.driverPickupTime ? `${item.deliveryDate || "N/A"} ${convertSlote(item.driverPickupTime)}` : "-",
          ReadyToShip: (formatDateFunction(item.readyToShipTime, "dd/mm/yyyy", true)) || "-",
          ShippedTime: (formatDateFunction(item.shippedTime, "dd/mm/yyyy", true)) || "-",
          CutomerOtpTime: (formatDateFunction(item.customerOtpTime, "dd/mm/yyyy", true)) || "-",
          DeliveredTime: (formatDateFunction(item.deliveredTime, "dd/mm/yyyy", true)) || "-",
          BranchLocation : item?.branchDetails?.lat && item?.branchDetails?.lng ? `${item.branchDetails.lat}-${item.branchDetails.lng}` : "N/A",
          CustomerLocation: getCustomerLocation(item),
        };
      });

      if (exportData.length > 0) {
        if (value === "csv") {
          exportCSV(exportData, "DriverOrdersReport.csv");
        } else if (value === "pdf") {
          const headers = [
            "Order Number",
            "Branch Name",
            "Driver Name",
            "Delivery Mode",
            "Dropzone",
            "Zone",
            "Payment Method",
            "Status",
            "Total Amount",
            "Order Date",
            "Delivery Date",
            "Time Slot",
            "Scheduled Pickup",
            "Ready To Ship",
            "Shipped Time",
            "Delivered Time",
            "Branch Location",
            "Customer Location"
          ];

          const rows = exportData.map((item) => [
            item.OrderNumber,
            item.BranchName,
            item.DriverName,
            item.DeliveryMode,
            item.Dropzone,
            item.Zone,
            item.PaymentMethod,
            item.Status,
            item.TotalAmount,
            item.OrderDate,
            item.DeliveryDate,
            item.TimeSlot,
            item.ScheduledPickup,
            item.ReadyToShip,
            item.ShippedTime,
            item.CutomerOtpTime,
            item.DeliveredTime,
            item.BranchLocation,
            item.CustomerLocation,
          ]);

          exportPDf(headers, rows, "DriverOrdersReport.pdf");
        }
      } else {
        toast.info("No data available for export.");
      }
    } else {
      toast.error(apiResponse?.message || "Failed to export data.");
    }
  } catch (e) {
    toast.error(e?.message || "Something went wrong while exporting.");
  }

  dispatch(changeLoader(false));
  };

  useEffect(() => {
    if (driverId) {
    fetchOrdersForDriverDetails();
    }
  }, [inputData, paginationModel, driverId, fetchOrdersForDriverDetails]);

  return (
    <>
      <Card>
        <div className="flex flex-wrap justify-between	mb-4 gap-5 ">
          <div className="">
            <Typography className="text-xl font-semibold flex gap-1" align={"left"}>
              <Link to="/report" className="!text-[#176B53]" state={{ 
                  inputData: inputData,
                  mainTabIndex: "1", 
                  subTabIndex: tabIndex 
                }}>
                Reports /{" "}
              </Link>
              <Typography
                onClick={() => navigate("/report", { state: { inputData: inputData,
                    mainTabIndex: "1",
                    subTabIndex: tabIndex 
                  } 
                })}
                className="!text-xl !text-[#176B53] cursor-pointer"
              >
                Sales Reports / Sales By Driver
              </Typography>
            </Typography>
          </div>
          <div className="flex flex-wrap gap-3">
            <Form form={form} layout="vertical">
              <Form.Item name="search">
                <div className="flex flex-wrap gap-3">
                  <RangePicker
                    className="cursor-pointer"
                    onChange={handleDateChange}
                    value={[
                      inputData?.start_date ? dayjs(inputData?.start_date, "YYYY-MM-DD") : null,
                      inputData?.end_date ? dayjs(inputData?.end_date, "YYYY-MM-DD") : null,
                    ]}
                    format="YYYY-MM-DD"
                    allowClear={false}
                  />
                  <Search
                    placeholder="Search"
                    style={{
                      width: window.innerWidth < 768 ? "100%" : 200,
                    }}
                    className="cursor-pointer"
                    onChange={(e) => {
                      const value = Validation.strFormat(e.target.value, "trim");
                      handleSearchDebounced(value);
                    }}
                  />
                </div>
              </Form.Item>
            </Form>
            <div className="flex flex-wrap gap-3">
              <>
                <Button
                  type="primary"
                  size={14}
                  icon={<DownloadOutlined />}
                  className="cursor-pointer"
                  onClick={() => {
                    exportData("csv");
                  }}
                >
                  Download in CSV
                </Button>
                <Button
                  type="primary"
                  size={14}
                  icon={<DownloadOutlined />}
                  className="cursor-pointer"
                  onClick={() => {
                    exportData("pdf");
                  }}
                >
                  Download in PDF
                </Button>
              </>
            </div>
          </div>
        </div>
        <Table columns={expandColumns} dataSource={dataSource} pagination={true} className="multi-tables responsive" />
      </Card>
    </>
  );
};
export default DriverDataReport;
